import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import { supabase } from '../../lib/supabase';

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ message, type, onDismiss }) => (
  <div className={`fixed bottom-24 right-6 z-[3000] flex items-center space-x-3 px-4 py-3 rounded-lg shadow-lg animate-fade-in max-w-sm ${
    type === 'success' ? 'bg-success text-white' :
    type === 'error' ? 'bg-error text-white' : 'bg-primary text-white'
  }`}>
    <Icon name={type === 'success' ? 'CheckCircle' : type === 'error' ? 'XCircle' : 'Info'} size={18} />
    <span className="text-sm font-medium flex-1">{message}</span>
    <button onClick={onDismiss} className="opacity-70 hover:opacity-100">
      <Icon name="X" size={14} />
    </button>
  </div>
);

// ─── System Broadcast Modal ───────────────────────────────────────────────────
const BroadcastModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({ title: '', message: '', audience: 'all' });
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!form?.title?.trim() || !form?.message?.trim()) return;
    setSending(true);
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      await supabase?.from('system_alerts')?.insert({
        title: form?.title,
        message: form?.message,
        severity: 'medium',
        category: 'broadcast',
        status: 'active',
        metadata: { audience: form?.audience },
        created_by: session?.user?.id || null,
      });
      onSuccess(`Broadcast sent to ${form?.audience === 'all' ? 'all users' : form?.audience === 'drivers' ? 'drivers only' : 'customers only'}.`);
      onClose();
    } catch (err) {
      console.error('Broadcast error:', err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[2000] flex items-center justify-center p-4">
      <div className="bg-surface rounded-lg shadow-modal max-w-md w-full animate-fade-in">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning-100 rounded-full flex items-center justify-center">
              <Icon name="Megaphone" size={20} color="var(--color-warning)" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-primary">System Broadcast</h3>
              <p className="text-sm text-text-muted">Send a message to platform users</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-surface-hover transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Message Title</label>
            <input
              type="text"
              value={form?.title}
              onChange={e => setForm(prev => ({ ...prev, title: e?.target?.value }))}
              className="input-field w-full"
              placeholder="e.g. Scheduled Maintenance Notice"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Message Body</label>
            <textarea
              value={form?.message}
              onChange={e => setForm(prev => ({ ...prev, message: e?.target?.value }))}
              className="input-field w-full resize-none"
              rows={4}
              placeholder="Enter your broadcast message..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">Audience</label>
            <select
              value={form?.audience}
              onChange={e => setForm(prev => ({ ...prev, audience: e?.target?.value }))}
              className="input-field w-full"
            >
              <option value="all">All Users</option>
              <option value="drivers">Drivers Only</option>
              <option value="customers">Customers Only</option>
            </select>
          </div>
        </div>
        <div className="p-6 border-t border-border flex space-x-3">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium border border-border rounded-lg hover:bg-surface-hover transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !form?.title?.trim() || !form?.message?.trim()}
            className="flex-1 px-4 py-2 text-sm font-medium bg-warning text-white rounded-lg hover:bg-warning-500 transition-colors disabled:opacity-50"
          >
            {sending ? 'Sending...' : 'Send Broadcast'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Emergency Modal ──────────────────────────────────────────────────────────
const EmergencyModal = ({ onClose, onSuccess }) => {
  const [processing, setProcessing] = useState(null);

  const emergencyActions = [
    { label: 'System-wide Alert', icon: 'AlertCircle', type: 'system_alert' },
    { label: 'Service Suspension', icon: 'PauseCircle', type: 'service_suspension' },
    { label: 'Emergency Contacts', icon: 'Phone', type: 'emergency_contacts' },
    { label: 'Incident Report', icon: 'FileText', type: 'incident_report' },
  ];

  const handleEmergencyAction = async (action) => {
    setProcessing(action?.type);
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      await supabase?.from('system_alerts')?.insert({
        title: `EMERGENCY: ${action?.label}`,
        message: `Emergency action triggered by admin: ${action?.label}`,
        severity: 'critical',
        category: action?.type,
        status: 'active',
        created_by: session?.user?.id || null,
      });
      onSuccess(`Emergency action "${action?.label}" triggered and logged.`);
      onClose();
    } catch (err) {
      console.error('Emergency action error:', err);
    } finally {
      setProcessing(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[2000] flex items-center justify-center p-4">
      <div className="bg-surface rounded-lg shadow-modal max-w-md w-full animate-fade-in">
        <div className="p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-error-100 rounded-full flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} color="#e53e3e" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-primary">Emergency Actions</h3>
              <p className="text-sm text-text-muted">Select an emergency response action</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-3">
          {emergencyActions?.map((action) => (
            <button
              key={action?.type}
              onClick={() => handleEmergencyAction(action)}
              disabled={processing === action?.type}
              className="w-full flex items-center space-x-3 p-3 rounded-lg border border-border hover:bg-error-50 hover:border-error transition-all duration-200 disabled:opacity-50"
            >
              <Icon name={action?.icon} size={18} color="#e53e3e" />
              <span className="text-sm font-medium text-text-primary flex-1 text-left">{action?.label}</span>
              {processing === action?.type && (
                <div className="w-4 h-4 border-2 border-error border-t-transparent rounded-full animate-spin" />
              )}
            </button>
          ))}
        </div>
        <div className="p-6 border-t border-border flex space-x-3">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium text-text-secondary border border-border rounded-lg hover:bg-surface-hover transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── QuickActionToolbar ───────────────────────────────────────────────────────
const QuickActionToolbar = ({ onMaintenanceChange }) => {
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Load maintenance mode state from platform_settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { data } = await supabase?.from('platform_settings')?.select('value')?.eq('key', 'maintenance_mode')?.single();
        const isOn = data?.value === 'true';
        setMaintenanceMode(isOn);
        if (onMaintenanceChange) onMaintenanceChange(isOn);
      } catch (err) {
        // table may not exist yet — silently ignore
      }
    };
    loadSettings();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleMaintenanceToggle = async () => {
    setMaintenanceLoading(true);
    const newState = !maintenanceMode;
    try {
      const { data: { session } } = await supabase?.auth?.getSession();

      // Persist to platform_settings
      await supabase?.from('platform_settings')?.upsert({ key: 'maintenance_mode', value: String(newState), updated_by: session?.user?.id, updated_at: new Date()?.toISOString() }, { onConflict: 'key' });

      // Insert system alert
      if (newState) {
        await supabase?.from('system_alerts')?.insert({
          title: 'Platform Under Maintenance',
          message: 'The platform has been placed under maintenance mode by an administrator. New bookings are temporarily suspended.',
          severity: 'critical',
          category: 'maintenance',
          status: 'active',
          created_by: session?.user?.id || null,
        });
      } else {
        // Resolve existing maintenance alerts
        await supabase?.from('system_alerts')?.update({ status: 'resolved', resolved_at: new Date()?.toISOString(), resolved_by: session?.user?.id })?.eq('category', 'maintenance')?.eq('status', 'active');
      }

      setMaintenanceMode(newState);
      if (onMaintenanceChange) onMaintenanceChange(newState);
      showToast(newState ? 'Maintenance mode activated.' : 'Maintenance mode deactivated.', newState ? 'error' : 'success');
      setShowQuickActions(false);
    } catch (err) {
      console.error('Maintenance toggle error:', err);
      showToast('Failed to toggle maintenance mode.', 'error');
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const handleSystemBackup = async () => {
    setBackupLoading(true);
    setShowQuickActions(false);
    try {
      // Fetch all data in parallel
      const [ordersRes, transactionsRes, driverProfilesRes, userProfilesRes] = await Promise.all([
        supabase?.from('orders')?.select('*')?.order('created_at', { ascending: false }),
        supabase?.from('wallet_transactions')?.select('*')?.order('created_at', { ascending: false }),
        supabase?.from('driver_profiles')?.select('*')?.order('created_at', { ascending: false }),
        supabase?.from('user_profiles')?.select('id, full_name, email, phone, role, created_at')?.order('created_at', { ascending: false }),
      ]);

      const toCSV = (rows, label) => {
        if (!rows || rows?.length === 0) return [`=== ${label} (0 records) ===`, '']?.join('\n');
        const headers = Object.keys(rows?.[0]);
        const lines = [
          `=== ${label} (${rows?.length} records) ===`,
          headers?.join(','),
          ...rows?.map(row => headers?.map(h => JSON.stringify(row?.[h] ?? ''))?.join(',')),
          '',
        ];
        return lines?.join('\n');
      };

      const csvContent = [
        `OnTheGo Full System Backup`,
        `Generated: ${new Date()?.toLocaleString()}`,
        '',
        toCSV(ordersRes?.data, 'ORDERS'),
        toCSV(transactionsRes?.data, 'WALLET TRANSACTIONS'),
        toCSV(driverProfilesRes?.data, 'DRIVER PROFILES'),
        toCSV(userProfilesRes?.data, 'USER PROFILES'),
      ]?.join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `onthego-backup-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(url);

      showToast('System backup downloaded successfully.', 'success');
    } catch (err) {
      console.error('Backup error:', err);
      showToast('Backup failed. Please try again.', 'error');
    } finally {
      setBackupLoading(false);
    }
  };

  const quickActions = [
    {
      id: 'emergency',
      label: 'Emergency Alert',
      icon: 'AlertTriangle',
      color: 'error',
      action: () => { setShowEmergencyModal(true); setShowQuickActions(false); },
    },
    {
      id: 'broadcast',
      label: 'System Broadcast',
      icon: 'Megaphone',
      color: 'warning',
      action: () => { setShowBroadcastModal(true); setShowQuickActions(false); },
    },
    {
      id: 'maintenance',
      label: maintenanceMode ? 'Disable Maintenance' : 'Maintenance Mode',
      icon: maintenanceMode ? 'CheckCircle' : 'Settings',
      color: maintenanceMode ? 'success' : 'secondary',
      action: handleMaintenanceToggle,
      loading: maintenanceLoading,
    },
    {
      id: 'backup',
      label: 'System Backup',
      icon: 'Database',
      color: 'primary',
      action: handleSystemBackup,
      loading: backupLoading,
    },
  ];

  return (
    <>
      {/* Toast */}
      {toast && <Toast message={toast?.message} type={toast?.type} onDismiss={() => setToast(null)} />}
      {/* Quick Action Button */}
      <div className="fixed bottom-6 right-6 z-[100]">
        <div className="relative">
          {showQuickActions && (
            <div className="absolute bottom-16 right-0 space-y-3 animate-fade-in">
              {quickActions?.map((action) => (
                <button
                  key={action?.id}
                  onClick={action?.action}
                  disabled={action?.loading}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg shadow-lg transition-all duration-200 hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed ${
                    action?.color === 'error' ? 'bg-error text-white hover:bg-error-500' :
                    action?.color === 'warning' ? 'bg-warning text-white hover:bg-warning-500' :
                    action?.color === 'secondary' ? 'bg-secondary text-white hover:bg-secondary-700' :
                    action?.color === 'success'? 'bg-success text-white hover:bg-success-500' : 'bg-primary text-white hover:bg-primary-700'
                  }`}
                  title={action?.label}
                >
                  {action?.loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Icon name={action?.icon} size={20} />
                  )}
                  <span className="text-sm font-medium whitespace-nowrap">{action?.label}</span>
                </button>
              ))}
            </div>
          )}

          <button
            onClick={() => setShowQuickActions(!showQuickActions)}
            className={`w-14 h-14 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center ${
              showQuickActions
                ? 'bg-error text-white rotate-45 hover:bg-error-500'
                : maintenanceMode
                ? 'bg-warning text-white hover:bg-warning-500' :'bg-accent text-primary hover:bg-accent-400'
            }`}
            aria-label="Quick actions"
          >
            <Icon name={showQuickActions ? 'X' : maintenanceMode ? 'AlertTriangle' : 'Zap'} size={24} />
          </button>
        </div>
      </div>
      {/* Broadcast Modal */}
      {showBroadcastModal && (
        <BroadcastModal
          onClose={() => setShowBroadcastModal(false)}
          onSuccess={(msg) => showToast(msg, 'success')}
        />
      )}
      {/* Emergency Modal */}
      {showEmergencyModal && (
        <EmergencyModal
          onClose={() => setShowEmergencyModal(false)}
          onSuccess={(msg) => showToast(msg, 'success')}
        />
      )}
    </>
  );
};

export default QuickActionToolbar;